import { fabric } from 'fabric';
import set from 'lodash-es/set';
import clone from 'lodash-es/clone';
import difference from 'lodash-es/difference';
import variables from '../variables';
import isEmpty from 'lodash-es/isEmpty';
import diff from '../../common/lib/deepDiff';

const PROPS_TO_EXCLUDE_FROM_OVERRIDES = [
  'angle',
  'scaleX',
  'scaleY',
  'left',
  'top',
  'height',
  'width',
  'renderedAsSVG',
  'skewX',
  'skewY',
  'cp',
];

const getPropsToExclude = object => {
  if (object.type === 'ludusCircle') {
    return [...PROPS_TO_EXCLUDE_FROM_OVERRIDES, 'rx', 'ry'];
  } else return PROPS_TO_EXCLUDE_FROM_OVERRIDES;
};

/**
 * Symbol object class
 *
 */
const LudusSymbol = fabric.util.createClass(fabric.Group, {
  type: 'ludusSymbol',
  borderColor: variables.colors.purple,
  cornerColor: 'white',
  cornerStrokeColor: variables.colors.purple,
  overrides: [],
  broken: false,
  lockUniScaling: true,

  /**
   * Class constructor
   *
   * @param reference
   * @param options
   */
  initialize(reference, options = {}) {
    this.reference = reference;
    this.overrides = options.overrides || [];
    this.cachedObjects = options.cachedObjects || [];
    this.referenceGroup = options.referenceGroup || null;
    this.options = options;
    // after cloning sometime, lockUniScaling is set to false
    // but group in viewer must have constant ratio (w/h)
    const groupOptions = { ...options };
    delete groupOptions.overrides;
    delete groupOptions.cachedObjects;
    delete groupOptions.referenceGroup;

    this.callSuper('initialize', options.objects, options, true);
    this.calcBounds();
    if (!this.broken) this.applyOverridenProps();
    // Register events
    this.on('mousedblclick', this.edit.bind(this));

    if (this.isEditing && !this.evented) {
      this.isEditing = false;
      this.evented = true;
    }
    this.on('removed', function(e) {
      this.isEditing && this.exitEditing();
    });
  },

  /**
   * Flag the symbol as broken, and use the cached objects stored in our API
   * instead of the (non-existing) source
   */
  flagAsBroken() {
    this._objects = [];
    this.broken = true;
    this.add(...this.cachedObjects);
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  },

  addCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    this.removeCanvasListeners();
    this.mouseDownHandler = this.mouseDownHandler.bind(this);
    this.canvas.on('mouse:down:before', this.mouseDownHandler);
    this.canvas.on('object:added', this.onObjectAdded);
  },

  removeCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    this.canvas.off('object:added', this.onObjectAdded);
    this.canvas.off('mouse:down:before', this.mouseDownHandler);
  },

  onObjectAdded() {
    if (this.editingSymbol) {
      this.editingSymbol.exitEditing();
    }
  },

  mouseDownHandler(e) {
    if (
      this.isEditing &&
      e.target !== this &&
      this.editingObjects &&
      !(
        this.editingObjects.some(o => o === e.target) ||
        e.target?.type === 'activeSelection'
      )
    ) {
      this.exitEditing();
      if (!e.target) {
        this.canvas.discardActiveObject();
        this.canvas.fire('selection:cleared', { target: this });
      }
    } else {
      return true;
    }
  },

  /**
   * Trigger edition - see registerEditionCallback below
   *
   */
  edit(e) {
    if (!this.isEditing) {
      this.enterEditing();
      const target = this.canvas.findTarget(e);
      target
        ? this.canvas.setActiveObject(target)
        : this.canvas.discardActiveObject();
    }
  },

  hasOverrides() {
    const _hasOverrides =
      this.overridenProps &&
      Object.values(this.overridenProps).filter(o => {
        return !isEmpty(o);
      });
    return _hasOverrides && _hasOverrides.length > 0;
  },

  resetOverrides() {
    if (this.hasOverrides()) {
      const originalContent = LudusSymbol.registered[this.reference].content;
      originalContent.objects.forEach(originalObject => {
        const instanceObject = this.objects.find(
          o => o.canvasId === originalObject.canvasId,
        );

        const overridenProps =
          this.overridenProps[instanceObject.ludus.id] || {};
        Object.keys(overridenProps).forEach(prop => {
          instanceObject[prop] = originalObject[prop];
          if (prop === 'fill' && instanceObject.updateGradient) {
            instanceObject.updateGradient();
          }
        });
        if (instanceObject.type === 'ludusImage') {
          instanceObject.reloadImage(() => {
            this.canvas.requestRenderAll();
          });
        }
        if (instanceObject.type === 'ludusText') {
          instanceObject.applyContents(instanceObject.contents);
        }
      });

      this.overridenProps = null;
      this.overrides = null;
      this.canvas.requestRenderAll();
      this.canvas.fire('object:modified', { target: this });
    }
    this.canvas.setActiveObject(this);
  },

  enterEditing() {
    if (this.canvas._activeObject === this) {
      this.canvas.discardActiveObject();
    }
    this.addCanvasListeners();
    this.isEditing = true;
    this.editingObjects = clone(this._objects);
    this.evented = false;
    this.hasControls = false;
    this._objects = [];
    this.editingObjects.forEach(obj => {
      const transform = obj.calcTransformMatrix();
      const options = fabric.util.qrDecompose(transform);
      obj.parentSymbol = this;
      obj.setOptions(options);
      obj.setPositionByOrigin(
        { x: options.translateX, y: options.translateY },
        'center',
        'center',
      );

      obj.lockMovementY = true;
      obj.lockScalingX = true;
      obj.lockScalingY = true;
      obj.lockRotation = true;
      obj.hasControls = false;

      delete obj.group;
      this.canvas.add(obj);
      obj.setCoords();
    });
    if (this.canvas) {
      this.canvas.editingSymbol = this;
      this.canvas.fire('symbol:editing:entered', { target: this });
    }
  },

  exitEditing() {
    this.canvas && this.canvas.discardActiveObject();
    this.removeCanvasListeners();
    this.selectable = true;
    this.isEditing = false;
    this.evented = true;
    this.visible = true;
    this.hasControls = true;
    this._objects = this.editingObjects;
    this.applyOverridenProps();
  },

  /**
   * Caching the group itself is not necessary, as its child will have caching.
   * Moreover caching groups produces a strange clipping behaviour (part of the content
   * of the group is sometimes clipped, not consistently). Without caching, no clipping
   * occurs.
   *
   * @returns {boolean}
   */
  shouldCache() {
    return false;
  },

  /**
   * Browse the provided node for a text child
   *
   * @param parent
   * @returns {Array}
   */
  _findTextChildren(parent) {
    let textBoxes = [];
    if (parent.ludus.type === 'group' || parent.ludus.type === 'symbol') {
      parent.forEachObject(child => {
        textBoxes = textBoxes.concat(this._findTextChildren(child));
      });
    } else if (['title', 'text', 'paragraph'].includes(parent.ludus.type)) {
      textBoxes = [parent];
    }

    return textBoxes;
  },

  /**
   * Return all text children
   *
   * @returns {*|Array}
   */
  getTextChildren() {
    return this._findTextChildren(this).sort((a, b) => {
      if (a.top === b.top) {
        return a.left < b.left ? -1 : 1;
      }

      return a.top < b.top ? -1 : 1;
    });
  },

  /**
   * Find a child by its id - recursive, useful to deal with overrides
   *
   * @param parent
   * @param childId
   * @returns {*}
   */
  findChild(parent, childId) {
    let child = null;

    if (parent.ludus.id === childId) {
      child = parent;
    } else if (
      parent.ludus.type === 'group' ||
      parent.ludus.type === 'symbol'
    ) {
      const newChildren = parent.getObjects();

      for (let i = 0; i < newChildren.length; i += 1) {
        const candidate = this.findChild(newChildren[i], childId);

        if (candidate !== null) {
          child = candidate;
          break;
        }
      }
    }

    return child;
  },

  calcBounds(sx, sy) {
    const referenceGroup = this.options.referenceGroup;
    const scalingFactorX =
      sx || this.width / referenceGroup?.width || this.width;
    const scalingFactorY =
      sy || this.height / referenceGroup?.height || this.height;
    this._objects.forEach(o =>
      o.scaleObjectInGroup({
        group: this,
        scaleX: scalingFactorX,
        scaleY: scalingFactorY,
        _by: null,
      }),
    );
  },

  applyOverridenProps() {
    const groupTransform = fabric.util.invertTransform(
      this.calcTransformMatrix(),
    );
    this._objects.forEach(obj => {
      obj.parentSymbol && delete obj.parentSymbol;
      this.canvas && this.canvas.remove(obj);
      const transform = obj.calcTransformMatrix();
      const mTransform = fabric.util.multiplyTransformMatrices(
        groupTransform,
        transform,
      );
      const options = fabric.util.qrDecompose(mTransform);
      obj.setOptions(options);
      obj.setPositionByOrigin(
        { x: options.translateX, y: options.translateY },
        'center',
        'center',
      );
      if (this.canvas) {
        obj.canvas = this.canvas; // needed to draw shadow properly (one visible impact, but probably others)
      }
      obj.group = this;

      // FIND DIFFERENCES
      const masterInstanceObject = LudusSymbol.registered[
        this.reference
      ]?.content?.objects?.find(o => o.canvasId === obj.canvasId);
      if (!masterInstanceObject) {
        return;
      }
      const serializedObj = obj.toObject(variables.propsToSave);
      const differences = diff(masterInstanceObject, serializedObj);

      const previousOverridenProps = this.overridenProps
        ? this.overridenProps[obj.ludus.id]
        : {};

      const differencesAsKeys = Object.keys(differences).filter(key => {
        return serializedObj[key] !== undefined;
      });
      const overridenValue = Object.fromEntries(
        difference(differencesAsKeys, getPropsToExclude(obj)).map(o => [
          o,
          true,
        ]),
      );

      if (overridenValue && JSON.stringify(overridenValue) !== '{}') {
        this.overridenProps = {
          ...this.overridenProps,
          [obj.ludus.id]: overridenValue,
        };
      }
      previousOverridenProps &&
        Object.keys(previousOverridenProps).forEach(prevProp => {
          if (!this.overridenProps[obj.ludus.id][prevProp]) {
            this.overridenProps[obj.ludus.id][prevProp] = null;
          }
        });
    });

    if (this.canvas && this.canvas.editingSymbol) {
      this.canvas.editingSymbol = null;
      this.canvas.fire('symbol:editing:exited', { target: this });
      this.canvas.fire('object:modified', { target: this });
      this.dirty = true;
    }
  },

  /**
   * Find the original value of a potentially overriden property
   *
   * @param childId
   * @param childProperty
   * @returns {*}
   */
  getOriginalPropertyValue(childId, childProperty) {
    const masterSymbol = LudusSymbol.registered[this.reference];
    if (masterSymbol) {
      const masterObjects = masterSymbol.content.objects;
      const originalObject = masterObjects.find(o => o.ludus?.id === childId);
      if (originalObject) {
        return originalObject[childProperty];
      }
    }

    return null;
  },

  /**
   * Usefull with undo/redo state
   *
   */
  applyMasterProps() {
    if (LudusSymbol.registered[this.reference]?.content?.objects) {
      LudusSymbol.registered[this.reference].content.objects.forEach(obj => {
        const instanceObject = this._objects?.find(
          o => o.canvasId === obj.canvasId,
        );
        if (instanceObject) {
          const serializedObj = instanceObject.toObject(variables.propsToSave);
          const differences = diff(obj, serializedObj);
          Object.keys(differences).forEach(key => {
            if (!this.overridenProps?.[obj.ludus.id]?.[key]) {
              instanceObject[key] = obj[key];
            }
          });
        } else {
          // in this case we dispatch this event to force a reload of the json and pass to ludusSymbol.fromObject properly
          this.canvas.fire('symbol:modified', {
            target: this,
          });
        }
      });

      this._objects = this._objects.filter(o =>
        LudusSymbol.registered[this.reference].content.objects.find(
          o2 => o2.canvasId === o.canvasId,
        ),
      );
    }

    this.calcBounds();
  },

  /**
   * Override fabric.Group.toObject() to include the reference
   *
   * @returns {Object}
   */
  toObject(propertiesToInclude = []) {
    if (this.overridenProps) {
      try {
        const overridenProps = { ...this.overridenProps };
        Object.keys(overridenProps).forEach(id => {
          if (!this._objects.find(obj => obj.ludus.id === id)) {
            overridenProps[id] = null;
          }
        });
        this.overridenProps = overridenProps;
      } catch (e) {
        console.info('Error trying to save luduSymbol', e.message);
      }
    }

    const propsToIncludes = Array.isArray(propertiesToInclude)
      ? propertiesToInclude
      : [];

    return this.callSuper(
      'toObject',
      propsToIncludes.concat([
        'reference',
        'name',
        'overrides',
        'isEditing',
        'overridenProps',
        'hash',
      ]),
    );
  },

  render(ctx) {
    ctx.save();
    ctx.globalAlpha = this.opacity;
    this.callSuper('render', ctx);
    ctx.restore();
  },
});

LudusSymbol.fromObject = (object, callback) => {
  const options = fabric.util.object.clone(object, true);
  const { reference } = options;
  if (LudusSymbol.registered && object.reference in LudusSymbol.registered) {
    const master = fabric.util.object.clone(
      LudusSymbol.registered[object.reference].content,
      true,
    );

    set(master, 'ludus.type', options.ludus.type);
    master.name = LudusSymbol.registered[object.reference].name;
    const masterObjects = master.objects.filter(Boolean);
    masterObjects.forEach(masterChild => {
      const instanceChild = object.objects?.find(
        o => o?.ludus?.id === masterChild?.ludus?.id,
      );
      if (instanceChild) {
        const childOverridenProps =
          options.overridenProps &&
          options.overridenProps[masterChild.ludus.id];

        /* OLD OVERRIDES
         With the old overrides, we stored an array as such: 
        overrides: [
           "childId": <childId>,
           "original": "Lorem ipsum",
           "overriddenProp": "text",
           "overriddenValue": "Dolores itae"
          ]
        We only use these overrides if no new (simpler) overridenProps are found (with a little twist for text, check below)
        The new OverridenProps (replacing overrides) are stored as an object:
        overridenProps: {
            <childId>: { nameOfProp: true || false || null } 
          }
        If a prop is declared as overriden it will use the value of the instance.
        Note that masterChild is the object we'll instantiate eventually. 
        */
        if (options.overrides) {
          [options.overrides].forEach(override => {
            const overridesObject = override.find(
              o => o.childId === instanceChild.ludus.id,
            );

            if (overridesObject) {
              const key = overridesObject.overriddenProp;
              const value =
                overridesObject.overriddenValue || overridesObject.original;
              if (
                !getPropsToExclude(masterChild).includes(key) &&
                (!childOverridenProps?.[key] ||
                  // if we have a text override, but not contents in the new overridenProps,
                  // it's just a save of the symbol that has not been edited using the latest ludusText  (with contents).
                  // We must keep the old overide as good value
                  (key === 'text' && !childOverridenProps?.contents))
              ) {
                // when we have an old override of text. The content is not synced (as we didn't update contents when updating text by mistake)
                // So we use the `text` as the good value and apply it to `contents`.
                // Then we know ludusText will use contents as main ref at init(and erase any potentiel wrong text value set via the new overridenProps)
                if (key === 'text') {
                  masterChild[key] = value;
                  masterChild.contents = fabric.LudusText.migrateTextToDelta(
                    value,
                    masterChild,
                  );
                }
              }
            }
          });
        }
        // NEW OVERRIDES

        if (childOverridenProps) {
          Object.keys(childOverridenProps).forEach(key => {
            if (!getPropsToExclude(masterChild).includes(key)) {
              masterChild[key] = instanceChild[key];
            }
          });
        }
      }
    });
    // find deleted object in master
    if (object.objects) {
      object.objects = object.objects.filter(
        o => !master.objects.find(m => m?.ludus?.id === o?.ludus?.id),
      );
    }

    fabric.util.enlivenObjects(masterObjects, contentArray => {
      const referenceGroup = master;
      if (callback) {
        callback(
          new LudusSymbol(reference, {
            ...options,
            hash: master.hash || '',
            name: master.name,
            type: 'ludusSymbol',
            objects: contentArray,
            referenceGroup,
          }),
        );
      }
    });
  } else {
    fabric.util.enlivenObjects(options.objects, contentArray => {
      if (callback) {
        callback(
          new LudusSymbol(reference, {
            ...options,
            type: 'ludusSymbol',
            broken: true,
            objects: contentArray,
            referenceGroup: options,
          }),
        );
      }
    });
  }
};

/**
 * As the edition will require the dispatching of a redux action, we will let an action creator
 * (see symbolActions.registerSymbols) register it.
 *
 * @param callback
 */
LudusSymbol.registerEditionCallback = callback => {
  LudusSymbol.editionCallback = callback;
};

/**
 * Symbols need to be registered so they can be used in a presentation. Ths method should be called:
 * - Whenever a symbol is created within the editor
 * - When loading a presentation, for each user/team symbol sent by the API
 *   (or at least the ones used in the presentation)
 *
 * @param reference
 * @param name
 * @param symbolContent: the fabric.Group serialized object that makes up the symbol content
 */
LudusSymbol.register = (reference, name, symbolContent) => {
  if (!('registered' in LudusSymbol)) {
    LudusSymbol.registered = {};
  }
  LudusSymbol.registered[reference] = { name, content: symbolContent };
};

/**
 * Check if a symbol has already been registered
 *
 * @type {never}
 */
LudusSymbol.isRegistered = reference =>
  'registered' in LudusSymbol && reference in LudusSymbol.registered;

/**
 * As advertised
 *
 * @param reference
 */
LudusSymbol.unregister = reference => {
  if ('registered' in LudusSymbol && reference in LudusSymbol.registered) {
    delete LudusSymbol.registered[reference];
  }
};

export default LudusSymbol;
